import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ChevronDown, 
  ChevronRight, 
  Eye, 
  Trash2, 
  Power, 
  PowerOff,
  QrCode,
  MessageSquare,
  Phone,
  Calendar,
  Loader2
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { API } from '../../App';

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sf, setSf] = useState('');
  const [pf, setPf] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [userDetails, setUserDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const t = localStorage.getItem('adminToken') || localStorage.getItem('admin_token') || localStorage.getItem('token');
      if (!t) {
        toast.error('Admin login required');
        navigate('/admin/login');
        return;
      }
      const q = new URLSearchParams({ page, limit: 20 });
      if (sf) q.append('status', sf);
      if (pf) q.append('subscription', pf);
      const r = await axios.get(`${API}/admin/users?${q}`, { headers: { Authorization: `Bearer ${t}` } });
      setUsers(r.data.users);
      setPages(r.data.pages);
      setTotal(r.data.total);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        toast.error('Admin session expired');
        navigate('/admin/login');
      } else {
        toast.error('Failed to load users');
      }
    }
    setLoading(false);
  }, [page, sf, pf, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const loadUserDetails = async (userId) => {
    if (userDetails[userId]) {
      // Already loaded, just toggle
      setExpandedUser(expandedUser === userId ? null : userId);
      return;
    }

    setLoadingDetails(prev => ({ ...prev, [userId]: true }));
    try {
      const t = localStorage.getItem('adminToken') || localStorage.getItem('admin_token') || localStorage.getItem('token');
      const r = await axios.get(`${API}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${t}` } });
      setUserDetails(prev => ({ ...prev, [userId]: r.data }));
      setExpandedUser(userId);
    } catch (e) {
      toast.error('Failed to load user details');
    }
    setLoadingDetails(prev => ({ ...prev, [userId]: false }));
  };

  const act = async (id, a) => {
    try {
      const t = localStorage.getItem('adminToken') || localStorage.getItem('admin_token') || localStorage.getItem('token');
      if (!t) {
        toast.error('Admin login required');
        navigate('/admin/login');
        return;
      }
      await axios.post(`${API}/admin/users/action`, { user_id: id, action: a }, { headers: { Authorization: `Bearer ${t}` } });
      toast.success(`User ${a}d successfully`);
      load();
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        toast.error('Admin session expired');
        navigate('/admin/login');
      } else {
        toast.error(`Failed to ${a} user`);
      }
    }
  };

  const deleteUser = async (userId) => {
    setDeleting(true);
    try {
      const t = localStorage.getItem('adminToken') || localStorage.getItem('admin_token') || localStorage.getItem('token');
      await axios.delete(`${API}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${t}` } });
      toast.success('User and all associated data deleted');
      setDeleteConfirm(null);
      load();
    } catch (e) {
      toast.error('Failed to delete user');
    }
    setDeleting(false);
  };

  const viewMessages = (qrCode) => {
    navigate(`/admin/messages?qr=${qrCode}`);
  };

  const viewCalls = (qrId) => {
    navigate(`/admin/calls?qr=${qrId}`);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white">User Management</h1>
          <p className="text-slate-400">View users, their QR codes, and activity</p>
        </div>

        <div className="flex gap-4">
          <select value={sf} onChange={e=>{setSf(e.target.value);setPage(1);}} className="bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2">
            <option value="">All Status</option>
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
          </select>
          <select value={pf} onChange={e=>{setPf(e.target.value);setPage(1);}} className="bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2">
            <option value="">All QR Status</option>
            <option value="active">Active QRs</option>
            <option value="expired">Expired QRs</option>
          </select>
          <span className="ml-auto text-slate-400">Total: {total} users</span>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="text-left text-slate-400 px-6 py-4 text-sm font-medium">User</th>
                <th className="text-left text-slate-400 px-6 py-4 text-sm font-medium">Status</th>
                <th className="text-right text-slate-400 px-6 py-4 text-sm font-medium">QR Codes</th>
                <th className="text-right text-slate-400 px-6 py-4 text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {loading && (
                <tr>
                  <td colSpan={4} className="text-center py-12">
                    <Loader2 className="w-6 h-6 text-slate-500 animate-spin mx-auto" />
                  </td>
                </tr>
              )}
              {!loading && users.length===0 && (
                <tr>
                  <td colSpan={4} className="text-center py-12 text-slate-400">
                    No users found
                  </td>
                </tr>
              )}
              {!loading && users.map(u=>(
                <React.Fragment key={u.id}>
                  <tr className="hover:bg-slate-800/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <button 
                          onClick={() => loadUserDetails(u.id)}
                          className="text-slate-400 hover:text-white transition-colors"
                        >
                          {expandedUser === u.id ? 
                            <ChevronDown className="w-5 h-5" /> : 
                            <ChevronRight className="w-5 h-5" />
                          }
                        </button>
                        <div>
                          <div className="text-white font-medium">{u.phone}</div>
                          <div className="text-slate-400 text-sm">
                            Joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        u.is_enabled !== false 
                          ? 'bg-emerald-500/20 text-emerald-400' 
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {u.is_enabled !== false ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-right">
                        <div className="text-white font-medium">{u.qr_count ?? 0} total</div>
                        <div className="text-sm text-slate-400">
                          <span className="text-emerald-400">{u.qr_active_count ?? 0} active</span>
                          {' • '}
                          <span className="text-amber-400">{u.qr_expired_count ?? 0} expired</span>
                          {(u.qr_disabled_count ?? 0) > 0 && (
                            <>{' • '}<span className="text-red-400">{u.qr_disabled_count} disabled</span></>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => loadUserDetails(u.id)}
                          className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-all"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {u.is_enabled !== false ? (
                          <button
                            onClick={() => act(u.id, 'disable')}
                            className="p-2 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 rounded-lg transition-all"
                            title="Disable User"
                          >
                            <PowerOff className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => act(u.id, 'enable')}
                            className="p-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 rounded-lg transition-all"
                            title="Enable User"
                          >
                            <Power className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => setDeleteConfirm(u.id)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all"
                          title="Delete User"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  
                  {/* Expanded Details Row */}
                  {expandedUser === u.id && (
                    <tr>
                      <td colSpan={4} className="bg-slate-900/50 px-6 py-4">
                        {loadingDetails[u.id] ? (
                          <div className="text-center py-8">
                            <Loader2 className="w-6 h-6 text-slate-500 animate-spin mx-auto" />
                          </div>
                        ) : userDetails[u.id]?.cars?.length > 0 ? (
                          <div className="space-y-4">
                            <h3 className="text-white font-semibold mb-3">QR Codes & Vehicles</h3>
                            <div className="grid gap-3">
                              {userDetails[u.id].cars.map(car => (
                                <div key={car.id} className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-3 mb-2">
                                        <QrCode className="w-5 h-5 text-amber-400" />
                                        <div>
                                          <div className="text-white font-medium">{car.nickname || 'Vehicle'}</div>
                                          {car.qr_code && (
                                            <div className="text-slate-400 text-sm font-mono">{car.qr_code}</div>
                                          )}
                                        </div>
                                      </div>
                                      
                                      <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                                        <div>
                                          <span className="text-slate-400">Subscription:</span>
                                          <span className={`ml-2 font-medium ${
                                            car.subscription_status === 'active' ? 'text-emerald-400' : 
                                            car.subscription_status === 'paid' ? 'text-amber-400' : 
                                            'text-red-400'
                                          }`}>
                                            {car.subscription_status || 'Free'}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="text-slate-400">Messages:</span>
                                          <span className="ml-2 text-white">{car.message_count || 0}</span>
                                        </div>
                                        <div>
                                          <span className="text-slate-400">Calls:</span>
                                          <span className="ml-2 text-white">{car.call_count || 0}</span>
                                        </div>
                                        {car.subscription_expiry && (
                                          <div>
                                            <span className="text-slate-400">Expires:</span>
                                            <span className="ml-2 text-white">
                                              {new Date(car.subscription_expiry).toLocaleDateString()}
                                            </span>
                                          </div>
                                        )}
                                        <div>
                                          <span className="text-slate-400">Status:</span>
                                          {car.subscription_status === 'disabled_by_admin' ? (
                                            <span className="ml-2 text-red-400 font-medium">Disabled by Admin</span>
                                          ) : (
                                            <span className={`ml-2 ${car.qr_active ? 'text-emerald-400' : 'text-red-400'}`}>
                                              {car.qr_active ? 'Active' : 'Inactive'}
                                            </span>
                                          )}
                                          {car.disable_reason && (
                                            <div className="text-xs text-red-400 mt-1">{car.disable_reason}</div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    <div className="flex items-center gap-2">
                                      {car.qr_code && (
                                        <button
                                          onClick={() => viewMessages(car.qr_code)}
                                          className="flex items-center gap-2 px-3 py-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-all text-sm"
                                        >
                                          <MessageSquare className="w-4 h-4" />
                                          View Messages
                                        </button>
                                      )}
                                      {car.call_qr_id && (
                                        <button
                                          onClick={() => viewCalls(car.call_qr_id)}
                                          className="flex items-center gap-2 px-3 py-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 rounded-lg transition-all text-sm"
                                        >
                                          <Phone className="w-4 h-4" />
                                          Calls History
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-slate-400">
                            No QR codes found for this user
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {pages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-700">
              <button 
                onClick={() => setPage(p => Math.max(1, p - 1))} 
                disabled={page === 1} 
                className="px-4 py-2 text-slate-400 hover:text-white disabled:opacity-50 transition-colors"
              >
                Previous
              </button>
              <span className="text-slate-400">Page {page} of {pages}</span>
              <button 
                onClick={() => setPage(p => Math.min(pages, p + 1))} 
                disabled={page === pages} 
                className="px-4 py-2 text-slate-400 hover:text-white disabled:opacity-50 transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-slate-700">
              <h2 className="text-xl font-bold text-white">Delete User</h2>
            </div>
            <div className="p-6">
              <p className="text-slate-300 mb-4">
                Are you sure you want to delete this user? This will permanently delete:
              </p>
              <ul className="list-disc list-inside text-slate-400 space-y-1 mb-6">
                <li>User account</li>
                <li>All QR codes</li>
                <li>All vehicles</li>
                <li>All messages and chats</li>
                <li>All payment records</li>
              </ul>
              <p className="text-red-400 font-semibold">This action cannot be undone.</p>
            </div>
            <div className="flex gap-3 p-6 border-t border-slate-700">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 px-4 py-3 border border-slate-600 text-slate-300 rounded-xl hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteUser(deleteConfirm)}
                disabled={deleting}
                className="flex-1 px-4 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleting ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
